import { supabase } from './supabase';
import {
  createAutomation,
  toggleAutomationLive,
  updateAutomation,
  saveAutomationTriggers,
  saveAutomationActions,
  saveWireConnections,
} from './automationsV2';
import type { AutomationV2, TriggerNode, ActionNode, WireConnection } from '../types/automationsV2';

/**
 * AI automation builder — client side.
 * Validates/normalizes flows returned by the ai-generate-automation edge
 * function and saves them through the exact same helpers the manual builder
 * uses (nodes + linear wire connections), so generated workflows execute
 * identically to hand-built ones.
 */

export interface AiFlowStep {
  id: string;
  type: string;
  name: string;
  config: Record<string, unknown>;
  summary: string;
}

export interface AiWorkflowFlow {
  name: string;
  description: string;
  overview: string;
  trigger: AiFlowStep;
  actions: AiFlowStep[];
  notes: string[];
}

export interface AiClarification {
  question: string;
  options: Array<{ label: string; detail: string }>;
}

export interface AiChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface AiGenerateResult {
  flow?: AiWorkflowFlow;
  clarification?: AiClarification;
  error?: string;
  insufficientTokens?: boolean;
  tokensUsed?: number;
}

const ALLOWED_TRIGGERS = new Set(['booking', 'feedback', 'form_submission', 'enter_crm_stage', 'missed_call', 'webhook']);
const ALLOWED_ACTIONS = new Set([
  'sms', 'email', 'delay', 'create_crm_lead', 'check', 'create_task', 'assign_tags', 'send_live_intake_form', 'feedback_agent',
]);

const TRIGGER_LABELS: Record<string, string> = {
  booking: 'Booking',
  feedback: 'Feedback Result',
  form_submission: 'Form Submission',
  enter_crm_stage: 'Enter CRM Stage',
  missed_call: 'Missed Call',
  webhook: 'Webhooks',
};
const ACTION_LABELS: Record<string, string> = {
  sms: 'SMS',
  email: 'Email',
  delay: 'Wait',
  create_crm_lead: 'Create CRM Lead',
  check: 'Check',
  create_task: 'Create Task',
  assign_tags: 'Assign Tags',
  send_live_intake_form: 'Send Live Intake Form',
  feedback_agent: 'Feedback Agent',
};

export function stepTypeLabel(type: string): string {
  return TRIGGER_LABELS[type] || ACTION_LABELS[type] || type;
}

/** Validate + normalize the raw JSON from the edge function. Throws with a readable message. */
export function normalizeAiFlow(raw: unknown): AiWorkflowFlow {
  const flow = raw as Partial<AiWorkflowFlow> | null;
  if (!flow || typeof flow !== 'object') throw new Error('The AI returned an empty workflow.');
  const trigger = flow.trigger as AiFlowStep | undefined;
  if (!trigger?.type || !ALLOWED_TRIGGERS.has(trigger.type)) {
    throw new Error(`The AI chose an unsupported trigger${trigger?.type ? ` (${trigger.type})` : ''}. Try rephrasing your request.`);
  }
  const actions = Array.isArray(flow.actions) ? (flow.actions as AiFlowStep[]) : [];
  if (actions.length === 0) throw new Error('The AI returned a workflow with no actions. Try adding more detail to your request.');
  for (const a of actions) {
    if (!a?.type || !ALLOWED_ACTIONS.has(a.type)) {
      throw new Error(`The AI included an unsupported step${a?.type ? ` (${a.type})` : ''}. Try rephrasing your request.`);
    }
  }

  // Normalize delay configs so the executor and the builder UI both read them.
  const normActions = actions.slice(0, 8).map((a, i) => {
    const config: Record<string, unknown> = { ...(a.config || {}) };
    if (a.type === 'delay') {
      const waitType = config.wait_type === 'reminder' ? 'reminder' : 'duration';
      config.wait_type = waitType;
      if (waitType === 'reminder') {
        const hours = Number(config.reminder_hours_before) || 24;
        config.reminder_hours_before = hours;
        config.reminder_reference = config.reminder_reference === 'drop_off_datetime' ? 'drop_off_datetime' : 'start_datetime';
      } else {
        let minutes = Number(config.duration_minutes) || 0;
        const unit = ['minutes', 'hours', 'days'].includes(String(config.unit)) ? String(config.unit) : 'hours';
        let amount = Number(config.duration) || 0;
        if (!minutes && amount) minutes = unit === 'days' ? amount * 1440 : unit === 'hours' ? amount * 60 : amount;
        if (!minutes) { minutes = 60; amount = 1; }
        if (!amount) amount = unit === 'days' ? Math.round(minutes / 1440) : unit === 'hours' ? Math.round(minutes / 60) : minutes;
        config.duration_minutes = minutes;
        config.unit = unit;
        config.duration = amount;
        config.duration_type = unit;
        config.displayDuration = `${amount} ${unit}`;
      }
    }
    if (a.type === 'create_crm_lead') {
      config.useCrmAdvancedSplit = false;
      config.useCrmAiRouting = false;
      if (config.selectedPipeline != null) config.selectedPipeline = Number(config.selectedPipeline);
      if (config.selectedStage != null) config.selectedStage = Number(config.selectedStage);
      if (!config.status) config.status = 'Open';
    }
    if (a.type === 'assign_tags' && !Array.isArray(config.conditions)) config.conditions = [];
    return {
      id: typeof a.id === 'string' && a.id ? a.id : `new-${i + 1}`,
      type: a.type,
      name: a.name || stepTypeLabel(a.type),
      config,
      summary: a.summary || '',
    };
  });

  // Webhook triggers need a generated endpoint id + secret (same format the
  // manual builder generates when the trigger is added).
  const triggerConfig: Record<string, unknown> = { ...(trigger.config || {}) };
  if (trigger.type === 'webhook' && !triggerConfig.webhook_id) {
    triggerConfig.webhook_id = `wh_${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`;
    triggerConfig.webhook_secret = `sk_${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`;
    triggerConfig.webhook_delivery = 'http';
  }

  return {
    name: (flow.name || 'AI Automation').toString().slice(0, 80),
    description: (flow.description || '').toString().slice(0, 200),
    overview: (flow.overview || '').toString(),
    trigger: {
      id: typeof trigger.id === 'string' && trigger.id ? trigger.id : 'new-trigger',
      type: trigger.type,
      name: trigger.name || stepTypeLabel(trigger.type),
      config: triggerConfig,
      summary: trigger.summary || '',
    },
    actions: normActions,
    notes: Array.isArray(flow.notes) ? flow.notes.map(String) : [],
  };
}

export async function requestAiWorkflow(params: {
  businessId: string;
  prompt: string;
  mode: 'generate' | 'edit';
  existingFlow?: AiWorkflowFlow | null;
  /** Prior clarification turns (assistant questions + user answers). */
  history?: AiChatTurn[];
}): Promise<AiGenerateResult> {
  if (!supabase) return { error: 'Supabase client not initialized' };
  try {
    const { data, error } = await supabase.functions.invoke('ai-generate-automation', {
      body: {
        mode: params.mode,
        prompt: params.prompt,
        businessId: params.businessId,
        existingFlow: params.existingFlow || undefined,
        history: params.history || [],
      },
    });
    if (error) return { error: error.message };
    if (!data?.success) {
      return { error: data?.error || 'Generation failed', insufficientTokens: !!data?.insufficientTokens };
    }
    if (data.clarification) {
      return { clarification: data.clarification as AiClarification, tokensUsed: data.tokensUsed };
    }
    return { flow: normalizeAiFlow(data.flow), tokensUsed: data.tokensUsed };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Generation failed' };
  }
}

/** Convert a validated AI flow into builder node arrays with a vertical layout. */
export function flowToNodes(flow: AiWorkflowFlow): { triggers: TriggerNode[]; actions: ActionNode[] } {
  const triggers: TriggerNode[] = [
    {
      id: flow.trigger.id,
      type: flow.trigger.type,
      name: flow.trigger.name,
      position: { x: 120, y: 80 },
      config: flow.trigger.config as Record<string, unknown>,
    },
  ];
  const actions: ActionNode[] = flow.actions.map((a, i) => ({
    id: a.id,
    type: a.type,
    name: a.name,
    position: { x: 120, y: 260 + i * 180 },
    config: a.config as Record<string, unknown>,
  }));
  return { triggers, actions };
}

/**
 * Persist an AI flow using the same save path as the manual builder.
 * For new automations pass automation = null; for edits pass the existing row.
 */
export async function saveAiWorkflow(params: {
  businessId: string;
  flow: AiWorkflowFlow;
  automation: AutomationV2 | null;
  goLive: boolean;
}): Promise<AutomationV2> {
  const { businessId, flow, goLive } = params;

  let automation = params.automation;
  if (!automation) {
    automation = await createAutomation({ name: flow.name, business_id: businessId, description: flow.description });
  } else if (automation.name !== flow.name) {
    try { automation = await updateAutomation(automation.id, { name: flow.name }); } catch { /* keep old name */ }
  }

  const { triggers, actions } = flowToNodes(flow);
  const triggerIdMap = await saveAutomationTriggers(automation.id, triggers);
  const actionIdMap = await saveAutomationActions(automation.id, actions);

  // Linear wiring: trigger → action1 → action2 → …
  const resolvedTriggerId = triggerIdMap.get(triggers[0].id) || triggers[0].id;
  const resolvedActionIds = actions.map((a) => actionIdMap.get(a.id) || a.id);
  const connections: WireConnection[] = [];
  let prev = resolvedTriggerId;
  for (let i = 0; i < resolvedActionIds.length; i++) {
    connections.push({
      id: `ai-wire-${i}`,
      fromNodeId: prev,
      toNodeId: resolvedActionIds[i],
      fromPort: 'outflow',
      toPort: 'inflow',
    });
    prev = resolvedActionIds[i];
  }
  await saveWireConnections(automation.id, connections);

  if (goLive) {
    automation = await toggleAutomationLive(automation.id, true);
  }
  return automation;
}

/** Build an AiWorkflowFlow from existing builder state (for edit mode context). */
export function nodesToFlow(
  automation: AutomationV2,
  triggers: TriggerNode[],
  actions: ActionNode[],
  wires: WireConnection[],
): AiWorkflowFlow | null {
  if (triggers.length !== 1) return null;
  // Order actions by walking the wires from the trigger.
  const nextOf = new Map<string, string>();
  for (const w of wires) nextOf.set(w.fromNodeId, w.toNodeId);
  const ordered: ActionNode[] = [];
  const byId = new Map(actions.map((a) => [a.id, a]));
  let cursor = nextOf.get(triggers[0].id);
  const seen = new Set<string>();
  while (cursor && byId.has(cursor) && !seen.has(cursor)) {
    seen.add(cursor);
    ordered.push(byId.get(cursor)!);
    cursor = nextOf.get(cursor);
  }
  // Include any actions not reached by wires so the AI still sees them.
  for (const a of actions) if (!seen.has(a.id)) ordered.push(a);

  return {
    name: automation.name,
    description: automation.description || '',
    overview: '',
    trigger: {
      id: triggers[0].id,
      type: triggers[0].type,
      name: triggers[0].name,
      config: triggers[0].config as Record<string, unknown>,
      summary: '',
    },
    actions: ordered.map((a) => ({
      id: a.id,
      type: a.type,
      name: a.name,
      config: a.config as Record<string, unknown>,
      summary: '',
    })),
    notes: [],
  };
}
